// Zählerliste – reine Funktionen: Zeilen aus der Abfrage, Suche, Typ-/Status-Filter, Sortierung, Export.
// Verhalten wie die bisherige ZaehlerListe (Suche über Nummer, Objekt, Standort, Einheit; Standard-Status „Aktiv“).

import type { ExportColumn, ExportStats } from '../export/types';
import { typInfo } from './typen';

export interface ZaehlerObjekt { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null; objektnummer: string | null }
export interface ZaehlerMieter { id: string; vorname: string | null; nachname: string | null }
export interface LetzteAblesung { wert: number; datum: string }

export interface ZaehlerRoh {
  id: string;
  zaehler_nummer: string;
  typ: string;
  einheit: string | null;
  standort: string | null;
  objekt_id: string;
  haus_id: string | null;
  einheit_bezeichnung: string | null;
  mieter_id: string | null;
  aktiv: boolean;
  notizen: string | null;
  created_at: string;
  updated_at: string;
  objekt: ZaehlerObjekt | null;
  mieter: ZaehlerMieter | null;
  /** neueste Ablesung (Abfrage mit Limit 1 je Zähler) */
  ablesungen?: { wert: number | string; datum: string }[] | null;
}

export interface ZaehlerZeile extends Omit<ZaehlerRoh, 'ablesungen'> {
  letzteAblesung: LetzteAblesung | null;
}

/** Spalten der Listenabfrage – die neueste Ablesung kommt eingebettet mit (keine Abfrage je Zähler). */
export const ZAEHLER_SELECT =
  '*, objekt:objekte(id, bezeichnung, strasse, hausnummer, objektnummer), mieter:mieter(id, vorname, nachname), ablesungen(wert, datum)';

export function baueZeile(r: ZaehlerRoh): ZaehlerZeile {
  const { ablesungen, ...rest } = r;
  const a = ablesungen?.[0];
  return { ...rest, letzteAblesung: a ? { wert: Number(a.wert), datum: a.datum } : null };
}

export type StatusFilter = 'alle' | 'aktiv' | 'inaktiv';
export const STATUS_OPTIONEN: readonly { key: StatusFilter; label: string }[] = [
  { key: 'alle', label: 'Alle Status' }, { key: 'aktiv', label: 'Aktiv' }, { key: 'inaktiv', label: 'Inaktiv' },
];
export const STANDARD_STATUS: StatusFilter = 'aktiv';
export const istStatusFilter = (v: string | null): v is StatusFilter => v === 'alle' || v === 'aktiv' || v === 'inaktiv';

export interface ListenFilter { suche: string; typ: string; status: StatusFilter }

export function filtereZaehler(zeilen: readonly ZaehlerZeile[], f: ListenFilter): ZaehlerZeile[] {
  const q = f.suche.trim().toLowerCase();
  return zeilen.filter(z => {
    if (f.status === 'aktiv' && !z.aktiv) return false;
    if (f.status === 'inaktiv' && z.aktiv) return false;
    if (f.typ && z.typ !== f.typ) return false;
    if (!q) return true;
    return [z.zaehler_nummer, z.objekt?.bezeichnung, z.standort, z.einheit_bezeichnung].some(t => t?.toLowerCase().includes(q));
  });
}

export type SortFeld = 'nummer' | 'typ' | 'objekt' | 'stand';
export type SortRichtung = 'asc' | 'desc';
export const istSortFeld = (v: string | null): v is SortFeld => v === 'nummer' || v === 'typ' || v === 'objekt' || v === 'stand';

/** Sortierung wie bisher: Nummer, Typ-Schlüssel, Objektname, letzter Stand (ohne Ablesung = 0). */
export function sortiereZaehler(zeilen: readonly ZaehlerZeile[], feld: SortFeld, richtung: SortRichtung): ZaehlerZeile[] {
  const wert = (z: ZaehlerZeile) => z.letzteAblesung?.wert ?? 0;
  const vergleich = (a: ZaehlerZeile, b: ZaehlerZeile): number => {
    switch (feld) {
      case 'nummer': return a.zaehler_nummer.localeCompare(b.zaehler_nummer);
      case 'typ': return a.typ.localeCompare(b.typ);
      case 'objekt': return (a.objekt?.bezeichnung ?? '').localeCompare(b.objekt?.bezeichnung ?? '');
      case 'stand': return wert(a) - wert(b);
    }
  };
  return [...zeilen].sort((a, b) => (richtung === 'asc' ? vergleich(a, b) : -vergleich(a, b)));
}

/** Klick auf einen Spaltenkopf: gleiche Spalte kehrt um, neue Spalte beginnt aufsteigend. */
export function naechsteSortierung(akt: { feld: SortFeld; richtung: SortRichtung }, feld: SortFeld): { feld: SortFeld; richtung: SortRichtung } {
  if (akt.feld === feld) return { feld, richtung: akt.richtung === 'asc' ? 'desc' : 'asc' };
  return { feld, richtung: 'asc' };
}

/** Typ-Filter: nur Typen, die vorkommen (wie bisher), nach Schlüssel sortiert. */
export function typOptionen(zeilen: readonly ZaehlerZeile[]): { key: string; label: string }[] {
  return [...new Set(zeilen.map(z => z.typ))].sort().map(t => ({ key: t, label: typInfo(t).label }));
}

/** „30.9.2026“ aus ISO-Datum (wie bisher toLocaleDateString, aber ohne Zeitzonenverschiebung). */
export function kurzDatum(iso: string | null | undefined): string {
  if (!iso) return '';
  const [j, m, t] = iso.slice(0, 10).split('-');
  return j && m && t ? `${Number(t)}.${Number(m)}.${j}` : iso;
}

/** „18.825,6 kWh“ */
export function standText(wert: number, einheit: string | null | undefined): string {
  return `${wert.toLocaleString('de-DE')} ${einheit ?? ''}`.trim();
}

/** „0001 · Alte Straße 17 · Whg 01“ */
export function ortText(z: Pick<ZaehlerZeile, 'objekt' | 'einheit_bezeichnung'>): string {
  return [z.objekt?.objektnummer, z.objekt?.bezeichnung, z.einheit_bezeichnung].filter(Boolean).join(' · ');
}

export const ZAEHLER_EXPORT_SPALTEN: ExportColumn[] = [
  { key: 'zaehlernummer', label: 'Zählernummer', format: (_: unknown, r: ZaehlerZeile) => r.zaehler_nummer || '' },
  { key: 'typ', label: 'Typ', format: (v: string) => typInfo(v).label },
  { key: 'objektnummer', label: 'Objektnr.', format: (_: unknown, r: ZaehlerZeile) => r.objekt?.objektnummer || '' },
  { key: 'objekt', label: 'Objekt', format: (_: unknown, r: ZaehlerZeile) => r.objekt?.bezeichnung || '' },
  { key: 'standort', label: 'Standort', format: (_: unknown, r: ZaehlerZeile) => r.standort || '' },
  { key: 'einheit_bezeichnung', label: 'Einheit', format: (v: string | null) => v || '' },
  { key: 'letzter_stand', label: 'Letzter Stand', format: (_: unknown, r: ZaehlerZeile) => (r.letzteAblesung ? standText(r.letzteAblesung.wert, r.einheit) : '') },
  {
    key: 'letzte_ablesung', label: 'Letzte Ablesung',
    format: (_: unknown, r: ZaehlerZeile) => {
      if (!r.letzteAblesung) return '';
      const [j, m, t] = r.letzteAblesung.datum.slice(0, 10).split('-');
      return `${t}.${m}.${j}`;
    },
  },
  { key: 'aktiv', label: 'Status', format: (v: boolean) => (v ? 'Aktiv' : 'Inaktiv') },
];

/** Statistik für „PDF (Bericht)“: Gesamt und je vorkommendem Typ (wie bisher). */
export function exportStatistik(sichtbar: readonly ZaehlerZeile[], alle: readonly ZaehlerZeile[]): ExportStats {
  return {
    items: [
      { label: 'Gesamt', value: sichtbar.length },
      ...typOptionen(alle).map(t => ({ label: t.label, value: sichtbar.filter(z => z.typ === t.key).length })),
    ],
  };
}
